/**
 * The farm auction's clock bar — drawn in `13a` at 1400px and in `13b` at 390px.
 *
 * It is deliberately the same object as the live draft's strip, in the same place and the same
 * shape, because the drawing says so in its own words: only the label changes from ON THE CLOCK to
 * BIDDING OPEN, and the countdown now belongs to the sale rather than the turn.
 *
 * What it has to do that the draft's strip does not: TWO PEOPLE MATTER PER TURN. The nominator
 * opened the lot; somebody else is winning it. So the bar names both — the nominator on the left
 * with the player, the high bidder on the right beside the clock — and never implies whose "go" it
 * is, because bidding is not a turn order.
 *
 * At 390px there is room for one sentence and a clock, so the wide detail drops out and the short
 * line stands in: `Amit nominated · 1.05`. Same `.wonly`/`.ponly` pair the live strip uses.
 */

import { faab } from '../domain/faab.js';
import { managerShort } from '../domain/identity.js';
import type { AuctionBar } from '../domain/farmAuction.js';

interface Props {
  bar: AuctionBar;
  /** Position and school, when the nominated player is one the prospect bank knows. */
  detail?: string;
  high: { gm: string; amount: number } | null;
  clock: { left: number; over: boolean; text: string } | null;
}

export function FarmBar({ bar, detail, high, clock }: Props) {
  return (
    <div className={`fbar ${bar.tone}`}>
      {/* The dot stays at 390px; the word does not — the line beside it already says it. */}
      <span className="tag">
        <i aria-hidden="true" />
        <span className="wonly">{bar.tag}</span>
      </span>

      {/* The phone (17h): the player and the price, or the short sentence when nobody is named. */}
      <span className="ponly line">
        {bar.player ? <b>{bar.player}</b> : bar.short}
      </span>

      {/* The drawn desktop line: the player, then who opened the lot and at which turn. */}
      <span className="wonly line">
        {bar.player ? (
          <>
            <b>{bar.player}</b>
            {detail && <span className="det">{detail}</span>}
            <span className="det">
              nominated by {managerShort(bar.nominator)} at <span className="mono">{bar.code}</span>
            </span>
          </>
        ) : (
          <>
            <b>{bar.tone === 'yours' ? 'Your nomination' : `${managerShort(bar.nominator)} is choosing`}</b>
            <span className="det mono">{bar.code}</span>
          </>
        )}
      </span>

      <span className="aside">
        {high && (
          <>
            <span className="wonly det">high bid</span>
            <span className="hb">
              <b>{faab(high.amount)}</b>
              <span className="det">{managerShort(high.gm)}</span>
            </span>
          </>
        )}
        {/*
          Blank rather than a guess: the going-gone length ships unset, and a bar that invented a
          countdown would be the one number on draft night nobody could check.
        */}
        {clock && <span className={`clk ${clock.over || clock.left <= 5 ? 'over' : ''}`}>
          {clock.text}
        </span>}
      </span>
    </div>
  );
}
